import { withTheme } from '@emotion/react'
import { Component } from 'react'
import styled from '@emotion/styled'
import { inject, observer } from 'mobx-react'
import { matchPath } from 'react-router'
import { withRouterProps } from '../../../../utils'
import { RailIcon, SVGWrapper } from '../../../../components'
import {
  ArrowRight,
  HomeSolid,
  OverallStrategyLive,
  Target,
  Account,
  Tools,
} from '../../../../assets/icons'
import { ReactComponent as HeyNicIcon } from '../../../../assets/icons/HeyNicIconOnly.svg'
import { FlexSpace } from './'

// One 50px slot per icon, matching the rail's spacing in the design.
const NavRailIcon = styled(RailIcon)`
  height: 50px;
`

// Holds the collapse toggle the same 74px of height in both states, so opening
// the sidebar doesn't shift the rail's icons.
const ToggleRow = styled.div`
  padding: 11px 0 19px;
`

const TogglePlaceholder = styled.div`
  height: 44px;
`

const LeftBarWrapper = styled.div`
  width: 64px;
  border: ${p => p.theme.sideBarBorder};
  background-color: ${p => p.theme.cardHoverBgColor};
  box-shadow: 0 2px 2px 0 rgba(182, 192, 196, 0.15);
  text-align: center;
  /* Above the content beside it so the collapsed on-hover labels, which escape
     the 64px rail to the right, paint over it. */
  position: relative;
  z-index: 5;
  display: flex;
  flex-direction: column;
`

class LeftSideBar extends Component {
  isActive = (...paths) =>
    paths.some(path => !!matchPath({ path, end: false }, this.props.location?.pathname ?? ''))

  render() {
    const {
      main: { sidebarActive, toggleSidebar },
      store: { features, config, aiStatus, hasPlanAccess },
    } = this.props
    const isSpendDownContext = config.isSpendown || config.spendDownModeling
    const defaultFill = this.props.theme?.primaryColor ?? '#038ab7'

    // Every icon below the toggle belongs to the collapsed rail only — the open
    // drawer lists the same destinations by name.
    const collapsed = !sidebarActive

    return (
      <LeftBarWrapper>
        <ToggleRow>
          {collapsed ? (
            <RailIcon label='Open sidebar' onClick={toggleSidebar}>
              <SVGWrapper svg={ArrowRight} size='custom30' margin='0px' fill={defaultFill} />
            </RailIcon>
          ) : (
            // The open drawer carries its own collapse arrow; the rail just
            // holds the space so its icons don't jump when the drawer opens.
            <TogglePlaceholder />
          )}
        </ToggleRow>
        {collapsed && (
          <NavRailIcon
            label='Home'
            isActive={this.isActive('/home')}
            onClick={() => this.props.navigate('/home')}>
            <SVGWrapper svg={HomeSolid} size='custom30' margin='0px' fill={defaultFill} />
          </NavRailIcon>
        )}
        {collapsed && isSpendDownContext && hasPlanAccess && (
          <NavRailIcon
            label='Overall Strategy'
            isActive={this.isActive('/overall', '/spending')}
            onClick={() => this.props.navigate('/overall')}>
            <SVGWrapper svg={OverallStrategyLive} size='custom30' margin='0px' fill={defaultFill} />
          </NavRailIcon>
        )}
        {collapsed && hasPlanAccess && (
          <NavRailIcon
            label='Goals'
            isActive={this.isActive('/goals')}
            onClick={() => this.props.navigate('/goals')}>
            <SVGWrapper svg={Target} size='custom30' margin='0px' fill={defaultFill} />
          </NavRailIcon>
        )}
        {collapsed && hasPlanAccess && (
          <NavRailIcon
            label='Accounts'
            isActive={this.isActive('/accounts')}
            onClick={() => this.props.navigate('/accounts')}>
            <SVGWrapper svg={Account} size='custom30' margin='0px' fill={defaultFill} />
          </NavRailIcon>
        )}
        <FlexSpace />
        {features.enableTools && (
          <NavRailIcon
            label='Tools'
            isActive={this.isActive('/tools')}
            onClick={() => this.props.navigate('/tools')}>
            <SVGWrapper svg={Tools} size='custom30' margin='0px' fill={defaultFill} />
          </NavRailIcon>
        )}
        {features.enableHeyNic && aiStatus?.available && (
          <NavRailIcon
            label='Hey Nic AI'
            isActive={this.isActive('/hey-nic')}
            onClick={() =>
              this.props.navigate('/hey-nic', {
                state: { from: this.props.location?.pathname },
              })
            }>
            {/* The Hey Nic icon is self-colored, so it renders directly. */}
            <HeyNicIcon />
          </NavRailIcon>
        )}
      </LeftBarWrapper>
    )
  }
}

export default withRouterProps(withTheme(inject('store', 'main')(observer(LeftSideBar))))
